import { Edit, Trash2, Sprout, Calendar, Scale, MapPin, Leaf, AlertTriangle, Heart } from "lucide-react";
import { Button } from "@/components/ui/button";
import StagePill from "@/components/StagePill";
import VeggieIcon from "@/components/VeggieIcon";
import { familyToken } from "./color-tokens";

/**
 * Read-only crop info panel rendered inside the chip popover.
 * Provides Edit and Delete callbacks back to the parent (BedGridEditor).
 */
export function CropPopoverContent({ crop, onEdit, onDelete }) {
    if (!crop) return null;
    const token = familyToken(crop.rotation_family || "other");
    const yieldRange =
        crop.expected_yield_low_kg != null && crop.expected_yield_high_kg != null
            ? `${crop.expected_yield_low_kg}–${crop.expected_yield_high_kg} kg`
            : null;
    const sowDate = crop.sowing_date_actual || crop.sowing_date_planned;
    const harvestStart = crop.harvest_window_start;
    const isPlaced = !!crop.position;
    const warnings = crop.antagonist_warnings || [];
    const companions = crop.companions || [];

    return (
        <div className="space-y-3 min-w-[260px]" data-testid={`crop-popover-${crop.id}`}>
            <header>
                <div className="flex items-start justify-between gap-2">
                    <div className="flex items-start gap-3">
                        <div className="w-16 h-16 rounded-lg bg-[var(--vm-input)]/50 border border-[var(--vm-border)] flex items-center justify-center shrink-0">
                            <VeggieIcon cropName={crop.crop_name} stage={crop.stage} size={64} />
                        </div>
                        <div>
                            <div className="font-serif-display text-xl text-[var(--vm-text)] leading-tight">
                                {crop.crop_name}
                            </div>
                            {crop.variety && (
                                <div className="text-xs italic text-[var(--vm-text-muted)] mt-0.5">
                                    '{crop.variety}'
                                </div>
                            )}
                        </div>
                    </div>
                    <span
                        className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full border text-[10px] uppercase tracking-wide font-semibold ${token.bg} ${token.border} ${token.text}`}
                    >
                        <Leaf className="w-3 h-3" strokeWidth={1.5} />
                        {token.label}
                    </span>
                </div>
                <div className="mt-2">
                    <StagePill stage={crop.stage} />
                </div>
            </header>

            <div className="grid grid-cols-2 gap-x-3 gap-y-1.5 text-xs text-[var(--vm-text-secondary)]">
                <div className="flex items-center gap-1.5">
                    <Sprout className="w-3 h-3 text-[var(--vm-accent)]" strokeWidth={1.5} />
                    <span>{crop.plant_count} plant{crop.plant_count === 1 ? "" : "s"}</span>
                </div>
                {crop.cell_capacity && (
                    <div className="flex items-center gap-1.5" title="Plants per ~30cm cell">
                        <span className="font-mono-grid text-[10px]">density</span>
                        <span>{crop.cell_capacity}/cell</span>
                    </div>
                )}
                {isPlaced && (
                    <div className="flex items-center gap-1.5">
                        <MapPin className="w-3 h-3" strokeWidth={1.5} />
                        <span>row {crop.position.row}, col {crop.position.col}</span>
                    </div>
                )}
                {sowDate && (
                    <div className="flex items-center gap-1.5">
                        <Calendar className="w-3 h-3" strokeWidth={1.5} />
                        <span>sow {sowDate}</span>
                    </div>
                )}
                {harvestStart && (
                    <div className="flex items-center gap-1.5">
                        <Calendar className="w-3 h-3" strokeWidth={1.5} />
                        <span>harvest {harvestStart}</span>
                    </div>
                )}
                {yieldRange && (
                    <div className="flex items-center gap-1.5">
                        <Scale className="w-3 h-3" strokeWidth={1.5} />
                        <span>{yieldRange}</span>
                    </div>
                )}
            </div>

            {crop.notes && (
                <div className="text-xs italic text-[var(--vm-text-secondary)] border-l-2 border-[var(--vm-sage)] pl-2">
                    {crop.notes}
                </div>
            )}

            {warnings.length > 0 && (
                <div
                    className="text-xs space-y-1 rounded-md border border-[var(--vm-secondary)]/40 bg-[var(--vm-secondary)]/10 px-3 py-2"
                    data-testid={`crop-warnings-${crop.id}`}
                >
                    <div className="flex items-center gap-1.5 font-semibold text-[var(--vm-secondary)]">
                        <AlertTriangle className="w-3.5 h-3.5" strokeWidth={1.5} />
                        Antagonist neighbour{warnings.length === 1 ? "" : "s"}
                    </div>
                    <ul className="ml-5 list-disc text-[var(--vm-text-secondary)]">
                        {warnings.map((w) => (
                            <li key={w.neighbor_id}>
                                <span className="font-medium text-[var(--vm-text)]">{w.neighbor_name}</span>
                                {w.neighbor_position && (
                                    <span className="opacity-70"> · row {w.neighbor_position.row}, col {w.neighbor_position.col}</span>
                                )}
                            </li>
                        ))}
                    </ul>
                </div>
            )}

            {companions.length > 0 && (
                <div className="text-xs flex items-start gap-1.5 text-[var(--vm-text-secondary)]">
                    <Heart className="w-3.5 h-3.5 text-[var(--vm-sage)] mt-0.5" strokeWidth={1.5} />
                    <span>
                        <span className="font-semibold text-[var(--vm-sage)]">Plant near:</span>{" "}
                        {companions.slice(0, 6).join(", ")}
                        {companions.length > 6 && "…"}
                    </span>
                </div>
            )}

            <div className="flex gap-2 pt-2 border-t border-[var(--vm-border)]">
                <Button
                    size="sm"
                    variant="ghost"
                    onClick={onEdit}
                    data-testid={`crop-popover-edit-${crop.id}`}
                    className="flex-1 text-[var(--vm-accent)] hover:text-[var(--vm-accent-hover)]"
                >
                    <Edit className="w-3.5 h-3.5 mr-1.5" /> Edit
                </Button>
                <Button
                    size="sm"
                    variant="ghost"
                    onClick={onDelete}
                    data-testid={`crop-popover-delete-${crop.id}`}
                    className="flex-1 text-[var(--vm-secondary)] hover:text-[var(--vm-secondary-hover)]"
                >
                    <Trash2 className="w-3.5 h-3.5 mr-1.5" /> Delete
                </Button>
            </div>
        </div>
    );
}
